/**
 * Crate Stock Utility Functions
 *
 * Centralized functions for managing crate stock operations including:
 * - Event logging (SOLD, RESTOCKED, ADJUSTED, etc.)
 * - Crate updates (selling, restocking)
 * - Event flushing and restoration
 *
 * This ensures consistency across all crate operations in the application.
 * For product countedStock updates, see productStock.js
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../configs/FirebaseConfig";
import {
  FIRESTORE_CRATE_STOCK_LOGS_TABLE,
  FIRESTORE_CRATE_STOCKS_TABLE,
  FIRESTORE_ORDERPICKINGS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../constants/FirestoreConstant";
import {
  LOG_ACTION_INCOMING_GOODS_REGISTERED,
  LOG_ACTION_QUANTITY_ADJUSTED,
  LOG_ACTION_RESTOCKED_UNPICK,
  LOG_ACTION_SOLD,
} from "../constants/CratesConstants";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";
import dayjs from "dayjs";
import { updateProductCountedStock } from "./productStock";

/**
 * Sanitizes crate ID by replacing slashes with underscores
 */
export const sanitizeCrateDocId = (crateId) => {
  if (!crateId) return crateId;
  return String(crateId).replaceAll("/", "_").trim();
};

/**
 * Gets the first item from a crate's items or products array
 * For fixed crates with multiple products, optionally pass productId or barcode to find specific item
 */
export const getCrateStockItem = (crate, productIdOrBarcode = null) => {
  if (!crate) return null;
  const itemsArray = Array.isArray(crate.items) ? crate.items : Array.isArray(crate.products) ? crate.products : [];
  if (!itemsArray.length) return null;

  // If no specific product requested, return first item (backwards compatible)
  if (!productIdOrBarcode) {
    return itemsArray[0] || null;
  }

  // Normalize the search term (remove leading zeros for barcode comparison)
  const normalizeBarcode = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const digits = str.replace(/\D/g, "");
    return digits.replace(/^0+/, "");
  };

  const searchTerm = normalizeBarcode(productIdOrBarcode);

  // Try to find item by productId first
  let item = itemsArray.find((i) => i?.productId === productIdOrBarcode);
  if (item) return item;

  // Try to find by normalized barcode
  if (searchTerm) {
    item = itemsArray.find((i) => normalizeBarcode(i?.barcode) === searchTerm);
    if (item) return item;
  }

  // If not found, return first item as fallback
  return itemsArray[0] || null;
};

/**
 * Gets a specific item from a crate by productId or barcode
 * Returns null if not found (no fallback to first item)
 */
export const getCrateStockItemByProduct = (crate, productIdOrBarcode) => {
  if (!crate || !productIdOrBarcode) return null;
  const itemsArray = Array.isArray(crate.items) ? crate.items : Array.isArray(crate.products) ? crate.products : [];
  if (!itemsArray.length) return null;

  const normalizeBarcode = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const digits = str.replace(/\D/g, "");
    return digits.replace(/^0+/, "");
  };

  const searchTerm = normalizeBarcode(productIdOrBarcode);

  // Try to find item by productId first
  let item = itemsArray.find((i) => i?.productId === productIdOrBarcode);
  if (item) return item;

  // Try to find by normalized barcode
  if (searchTerm) {
    item = itemsArray.find((i) => normalizeBarcode(i?.barcode) === searchTerm);
    if (item) return item;
  }

  return null;
};

/**
 * Creates a standardized SOLD event object
 */
export const createSoldEvent = ({
  crateId,
  crateDocId,
  productId,
  productName,
  productBarcode,
  packageDate,
  packageDateUnix,
  quantityBefore,
  quantity,
  delta,
  orderId,
  farmorderId,
  company,
  createdByUid,
  createdBy,
  source = "ORDER_PICKING",
}) => ({
  type: LOG_ACTION_SOLD,
  crateId: crateId || crateDocId,
  productId: productId || null,
  productName: productName || null,
  productBarcode: productBarcode || null,
  packageDate: packageDate || null,
  packageDateUnix: packageDateUnix || null,
  quantityBefore,
  quantity,
  delta,
  source,
  orderId: orderId || null,
  farmorderId: farmorderId || null,
  company: company || null,
  createdByUid: createdByUid || null,
  createdBy: createdBy || null,
  createdAt: dayjs().unix(),
});

/**
 * Creates a standardized RESTOCKED_UNPICK event object
 */
export const createRestockEvent = ({
  crateId,
  crateDocId,
  productId,
  productName,
  productBarcode,
  quantityBefore,
  quantity,
  delta,
  orderId,
  farmorderId,
  company,
  createdByUid,
  createdBy,
  source = "ORDER_PICKING",
}) => ({
  type: LOG_ACTION_RESTOCKED_UNPICK,
  crateId: crateId || crateDocId,
  productId,
  productName: productName || null,
  productBarcode: productBarcode || null,
  quantityBefore,
  quantity,
  delta,
  source,
  orderId: orderId || null,
  farmorderId: farmorderId || null,
  company: company || null,
  createdByUid: createdByUid || null,
  createdBy: createdBy || null,
  createdAt: dayjs().unix(),
});

/**
 * Creates a standardized QUANTITY_ADJUSTED event object
 */
export const createAdjustmentEvent = ({
  crateId,
  crateDocId,
  productId,
  productName,
  productBarcode,
  quantityBefore,
  quantity,
  delta,
  reason,
  company,
  createdByUid,
  createdBy,
}) => ({
  type: LOG_ACTION_QUANTITY_ADJUSTED,
  crateId: crateId || crateDocId,
  productId: productId || null,
  productName: productName || null,
  productBarcode: productBarcode || null,
  quantityBefore,
  quantity,
  delta,
  reason: reason || null,
  company: company || null,
  createdByUid: createdByUid || null,
  createdBy: createdBy || null,
  createdAt: dayjs().unix(),
});

/**
 * Creates a standardized INCOMING_GOODS_REGISTERED event object
 */
export const createIncomingGoodsEvent = ({
  crateId,
  crateDocId,
  productId,
  productName,
  productBarcode,
  quantityBefore,
  quantity,
  delta,
  packageDate,
  packageDateUnix,
  company,
  createdByUid,
  createdBy,
}) => ({
  type: LOG_ACTION_INCOMING_GOODS_REGISTERED,
  crateId: crateId || crateDocId,
  productId: productId || null,
  productName: productName || null,
  productBarcode: productBarcode || null,
  quantityBefore,
  quantity,
  delta,
  packageDate: packageDate || null,
  packageDateUnix: packageDateUnix || null,
  company: company || null,
  createdByUid: createdByUid || null,
  createdBy: createdBy || null,
  createdAt: dayjs().unix(),
});

/**
 * Logs an event to a crate's events subcollection
 */
export const logCrateEvent = async ({ crateDocId, eventData }) => {
  if (!crateDocId) throw new Error("crateDocId is required");
  if (!eventData) throw new Error("eventData is required");

  const eventsColRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
    "events",
  );

  await addDoc(eventsColRef, eventData);
};

/**
 * Updates a crate's status and items
 */
export const updateCrateStock = async ({ crateDocId, status, items, itemsCount, updatedBy }) => {
  if (!crateDocId) throw new Error("crateDocId is required");

  const crateRef = doc(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
  );

  await updateDoc(crateRef, {
    status,
    items,
    itemsCount,
    updatedAt: dayjs().unix(),
    updatedBy: updatedBy || null,
  });
};

/**
 * Applies a SOLD action to a crate:
 * - Decreases quantity in crate
 * - Logs SOLD event
 * - Flushes events to log if crate becomes empty
 *
 * Returns metadata about the sold operation
 */
export const applySoldToCrate = async ({
  crate,
  soldQty,
  productIdOrBarcode,
  orderId,
  farmorderId,
  userEmail,
  userId,
}) => {
  if (!crate) throw new Error("Missing crate");
  const qty = Number(soldQty || 0);
  if (qty <= 0) return;

  const crateDocId = crate.id || sanitizeCrateDocId(crate.crateId);
  const crateRef = doc(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
  );

  // Get latest crate data
  const latestSnap = await getDoc(crateRef);
  const latest = latestSnap.data();
  if (!latest) throw new Error("Crate not found");

  const latestItems = Array.isArray(latest.items)
    ? latest.items
    : Array.isArray(latest.products)
      ? latest.products
      : [];

  // Find the specific item in the crate (important for fixed crates with multiple products)
  // Use getCrateStockItem which handles product lookup by ID or barcode
  const item = getCrateStockItem({ items: latestItems }, productIdOrBarcode);
  if (!item) throw new Error(`Product ${productIdOrBarcode} not found in crate ${crateDocId}`);

  const currentItemQty = Number(item?.quantity || 0);
  const nextItemQty = Math.max(0, currentItemQty - qty);

  // Update crate: update the specific item or remove it if quantity becomes 0
  const nextItems = latestItems
    .map((i) => {
      // Check if this is the item we're updating (by productId or barcode)
      const isTargetItem =
        i.productId === item.productId ||
        (i.barcode && item.barcode && normalizeBarcode(i.barcode) === normalizeBarcode(item.barcode));

      if (isTargetItem) {
        return nextItemQty > 0 ? { ...i, quantity: nextItemQty } : null;
      }
      return i;
    })
    .filter(Boolean);

  const nextItemsCount = nextItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);

  // Helper function to normalize barcodes
  function normalizeBarcode(value) {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const digits = str.replace(/\D/g, "");
    return digits.replace(/^0+/, "");
  }

  await updateCrateStock({
    crateDocId,
    status: nextItemsCount > 0 ? "in_use" : "available",
    items: nextItems,
    itemsCount: nextItemsCount,
    updatedBy: userId || userEmail || null,
  });

  // Log SOLD event
  const soldEvent = createSoldEvent({
    crateId: latest.crateId || crateDocId,
    crateDocId,
    productId: item?.productId || null,
    productName: item?.productName || null,
    productBarcode: item?.barcode || null,
    packageDate: item?.packageDate || null,
    packageDateUnix: item?.packageDateUnix || null,
    quantityBefore: currentItemQty,
    quantity: nextItemQty,
    delta: -qty,
    orderId,
    farmorderId,
    company: latest.company || null,
    createdByUid: userId || null,
    createdBy: userEmail || null,
  });

  await logCrateEvent({ crateDocId, eventData: soldEvent });

  // Flush events to log if crate is now empty
  if (nextItemsCount === 0) {
    await flushCrateEventsToLog({
      crateDocId,
      crateSnapshot: latest,
      productId: item?.productId || null,
      productBarcode: item?.barcode || null,
    });
    await updateCrateStock({
      crateDocId,
      status: "available",
      items: [],
      itemsCount: 0,
      updatedBy: userId || userEmail || null,
    });
  }

  return {
    crateDocId,
    crateId: latest.crateId || crateDocId,
    quantity: qty,
    productId: item?.productId || null,
    productBarcode: item?.barcode || null,
  };
};

/**
 * Restocks a crate with items:
 * - Increases quantity in crate
 * - Logs RESTOCKED_UNPICK event
 * - Restores item metadata if crate was empty
 */
export const restockCrate = async ({
  crateDocId,
  crateId,
  productId,
  productName,
  productBarcode,
  quantity,
  orderId,
  farmorderId,
  company,
  userEmail,
  userId,
  itemMetadata = {},
}) => {
  if (!crateDocId) throw new Error("crateDocId is required");
  const qty = Number(quantity || 0);
  if (qty <= 0) return;

  const crateRef = doc(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
  );

  // Get current crate data
  const crateSnap = await getDoc(crateRef);
  const crateData = crateSnap.data() || {};
  const crateItems = Array.isArray(crateData.items)
    ? crateData.items
    : Array.isArray(crateData.products)
      ? crateData.products
      : [];

  const isFixedCrate = crateData.crateType === "fixed_dumping" || crateData.isFixedDumpingCrate === true;

  let nextItems;
  let currentQty = 0;
  let nextQty = 0;

  if (isFixedCrate) {
    // Fixed dumping crates can hold multiple different products
    // Check if this product already exists in the crate
    const existingItemIndex = crateItems.findIndex((item) => item.productId === productId);

    if (existingItemIndex >= 0) {
      // Product already exists - update quantity
      nextItems = [...crateItems];
      const existingItem = nextItems[existingItemIndex];
      currentQty = Number(existingItem?.quantity || 0);
      nextQty = currentQty + qty;
      nextItems[existingItemIndex] = {
        ...itemMetadata,
        ...existingItem,
        productId: itemMetadata?.productId || existingItem?.productId || productId,
        barcode: itemMetadata?.barcode || existingItem?.barcode || productBarcode || null,
        quantity: nextQty,
      };
    } else {
      // New product - add to array
      currentQty = 0;
      nextQty = qty;
      nextItems = [
        ...crateItems,
        {
          ...itemMetadata,
          productId: itemMetadata?.productId || productId,
          barcode: itemMetadata?.barcode || productBarcode || null,
          productName: itemMetadata?.productName || productName || null,
          quantity: qty,
        },
      ];
    }
  } else {
    // Regular crates can only hold one product type
    const currentItem = crateItems[0] || null;
    currentQty = Number(currentItem?.quantity || 0);
    nextQty = currentQty + qty;

    nextItems = [
      {
        ...itemMetadata,
        ...currentItem,
        productId: itemMetadata?.productId || currentItem?.productId || productId,
        barcode: itemMetadata?.barcode || currentItem?.barcode || productBarcode || null,
        quantity: nextQty,
      },
    ];
  }

  const nextItemsCount = nextItems.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

  // Update crate
  await updateCrateStock({
    crateDocId,
    status: nextItemsCount > 0 ? "in_use" : "available",
    items: nextItems,
    itemsCount: nextItemsCount,
    updatedBy: userId || userEmail || null,
  });

  // Log RESTOCKED event
  const restockEvent = createRestockEvent({
    crateId: crateData.crateId || crateId || crateDocId,
    crateDocId,
    productId,
    productName: productName || null,
    productBarcode: productBarcode || null,
    quantityBefore: currentQty,
    quantity: nextQty,
    delta: qty,
    orderId,
    farmorderId,
    company: company || crateData.company || null,
    createdByUid: userId || null,
    createdBy: userEmail || null,
  });

  await logCrateEvent({ crateDocId, eventData: restockEvent });
};

/**
 * Flushes all events from a crate's subcollection to the crateStockLog collection
 * and deletes the events from the subcollection
 * For fixed crates with multiple products, pass productId and productBarcode to log the correct product
 */
export const flushCrateEventsToLog = async ({ crateDocId, crateSnapshot, productId = null, productBarcode = null }) => {
  if (!crateDocId) throw new Error("crateDocId is required");

  const eventsColRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
    "events",
  );

  const eventsSnap = await getDocs(eventsColRef);
  const events = [];
  eventsSnap.forEach((d) => {
    events.push({ id: d.id, ...d.data() });
  });

  // Only create log if there are events
  if (events.length > 0) {
    const logColRef = collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CRATE_STOCK_LOGS_TABLE,
    );

    await addDoc(logColRef, {
      crateId: crateSnapshot?.crateId || crateDocId,
      crateDocId,
      company: crateSnapshot?.company || null,
      productId: productId || getCrateStockItem(crateSnapshot)?.productId || null,
      productBarcode: productBarcode || getCrateStockItem(crateSnapshot)?.barcode || null,
      events,
      createdAt: dayjs().unix(),
    });
  }

  // Delete all events from subcollection
  const batch = writeBatch(firestore);
  eventsSnap.forEach((d) => {
    batch.delete(d.ref);
  });
  await batch.commit();
};

/**
 * Restores events from the latest crateStockLog entry back to the crate's events subcollection
 * Returns { restored: true, events: [...] } if successful, false if no log found
 */
export const restoreCrateEventsFromLog = async ({ crateDocId }) => {
  if (!crateDocId) return false;

  const logColRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCK_LOGS_TABLE,
  );

  const q = query(logColRef, where("crateDocId", "==", crateDocId), orderBy("createdAt", "desc"), limit(1));
  const snap = await getDocs(q);
  const logDoc = snap.docs?.[0] || null;
  if (!logDoc) return false;

  const logData = logDoc.data() || {};
  const events = Array.isArray(logData.events) ? logData.events : [];
  if (!events.length) {
    await deleteDoc(logDoc.ref);
    return false;
  }

  // Restore events to subcollection
  const eventsColRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
    "events",
  );

  const batch = writeBatch(firestore);
  events.forEach((ev) => {
    const eventId = ev?.id ? String(ev.id) : null;
    const { id, ...rest } = ev || {};
    const eventRef = eventId ? doc(eventsColRef, eventId) : doc(eventsColRef);
    batch.set(eventRef, rest, { merge: false });
  });

  batch.delete(logDoc.ref);
  await batch.commit();

  return { restored: true, events };
};

/**
 * Appends a crate pick event to an orderPicking document's cratePickEvents array
 */
export const appendOrderPickingEvent = async ({
  orderId,
  type = LOG_ACTION_SOLD,
  productId,
  bundleProductId,
  crateDocId,
  crateId,
  quantity,
  productBarcode,
  remainingQuantity,
  packageDateUnix,
  userEmail,
}) => {
  if (!orderId) throw new Error("orderId is required");

  const docRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERPICKINGS_TABLE, orderId);

  const snap = await getDoc(docRef);
  const data = snap.data() || {};
  const existing = Array.isArray(data.cratePickEvents) ? data.cratePickEvents : [];

  const next = [
    ...existing,
    {
      type,
      productId,
      ...(bundleProductId ? { bundleProductId } : {}),
      productBarcode: productBarcode || null,
      crateDocId,
      crateId: crateId || crateDocId,
      quantity: Number(quantity || 0),
      remainingQuantity: Number(remainingQuantity ?? quantity ?? 0),
      packageDateUnix: packageDateUnix || null,
      userEmail: userEmail || null,
      createdAtUnix: dayjs().unix(),
    },
  ];

  await updateDoc(docRef, {
    cratePickEvents: next,
    updatedAt: dayjs().unix(),
    updatedBy: userEmail,
  });

  return next;
};

/**
 * Gets all events from a crate's events subcollection
 */
export const getCrateEvents = async ({ crateDocId }) => {
  if (!crateDocId) return [];

  const eventsColRef = collection(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_CRATE_STOCKS_TABLE,
    crateDocId,
    "events",
  );

  const eventsSnap = await getDocs(eventsColRef);
  const events = [];
  eventsSnap.forEach((d) => {
    events.push({ id: d.id, ...d.data() });
  });

  return events;
};

/**
 * Extracts item metadata from crate events (for restocking empty crates)
 * Looks for the most recent INCOMING_GOODS_REGISTERED or SOLD event
 */
export const extractItemMetadataFromEvents = (events, productId) => {
  if (!Array.isArray(events) || !events.length) return {};

  const relevantEvent = events
    .filter(
      (e) =>
        (e.type === LOG_ACTION_INCOMING_GOODS_REGISTERED || e.type === LOG_ACTION_SOLD) && e.productId === productId,
    )
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];

  if (!relevantEvent) return {};

  const metadata = {
    productId: relevantEvent.productId,
    productName: relevantEvent.productName,
    barcode: relevantEvent.productBarcode,
    packageDate: relevantEvent.packageDate,
    packageDateUnix: relevantEvent.packageDateUnix,
    createdBy: relevantEvent.createdBy,
    createdByName: relevantEvent.createdBy,
    createdByUid: relevantEvent.createdByUid,
  };

  // Remove undefined values to prevent Firestore errors
  return Object.fromEntries(Object.entries(metadata).filter(([_, value]) => value !== undefined));
};

/**
 * Checks if a crate can receive restocked items
 * Returns { canRestock: boolean, reason: string|null, currentStatus: string, currentQuantity: number }
 */
export const canRestockToCrate = async (crateDocId) => {
  if (!crateDocId) {
    return { canRestock: false, reason: "No crate specified", currentStatus: null, currentQuantity: 0 };
  }

  try {
    const crateRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CRATE_STOCKS_TABLE,
      crateDocId,
    );

    const crateSnap = await getDoc(crateRef);

    if (!crateSnap.exists()) {
      // New crate - can always restock
      return { canRestock: true, reason: null, currentStatus: "new", currentQuantity: 0 };
    }

    const crateData = crateSnap.data();
    const status = crateData?.status || "available";
    const items = Array.isArray(crateData?.items) ? crateData.items : [];
    const currentQuantity = items.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);

    // Can restock if:
    // 1. Status is "available" and empty (quantity = 0)
    // 2. Status is "in_use" with the same product type (checked separately)
    if (status === "available" && currentQuantity === 0) {
      return { canRestock: true, reason: null, currentStatus: status, currentQuantity };
    }

    if (status === "lost") {
      return { canRestock: false, reason: "Crate is marked as lost", currentStatus: status, currentQuantity };
    }

    // Crate is in use or has items - need to check product compatibility separately
    return {
      canRestock: true,
      reason: null,
      currentStatus: status,
      currentQuantity,
      requiresProductCheck: true,
    };
  } catch (error) {
    console.error("Error checking crate availability:", error);
    return { canRestock: false, reason: error.message, currentStatus: "error", currentQuantity: 0 };
  }
};

/**
 * Checks if a crate can receive a specific product with specific package date
 * For restocking, we need the same product AND same package date (for shelf life)
 * EXCEPT for fixed dumping crates which can hold multiple different products
 */
export const canRestockProductToCrate = async (crateDocId, productId, packageDateUnix) => {
  const baseCheck = await canRestockToCrate(crateDocId);

  if (!baseCheck.canRestock) {
    return baseCheck;
  }

  // If new/empty crate, any product is fine
  if (baseCheck.currentStatus === "new" || baseCheck.currentQuantity === 0) {
    return { ...baseCheck, productCompatible: true, packageDateMatch: true };
  }

  try {
    const crateRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CRATE_STOCKS_TABLE,
      crateDocId,
    );

    const crateSnap = await getDoc(crateRef);
    const crateData = crateSnap.data();
    const isFixedCrate = crateData?.crateType === "fixed_dumping" || crateData?.isFixedDumpingCrate === true;

    // Fixed dumping crates can hold multiple different products - always allow
    if (isFixedCrate) {
      return { ...baseCheck, productCompatible: true, packageDateMatch: true, isFixedCrate: true };
    }

    // Regular crates: check product and package date compatibility
    const items = Array.isArray(crateData?.items) ? crateData.items : [];
    const existingItem = items[0];

    // Check product compatibility
    const productCompatible = existingItem?.productId === productId;

    // Check package date compatibility (for shelf life)
    const existingPackageDate = existingItem?.packageDateUnix;
    const packageDateMatch = !packageDateUnix || !existingPackageDate || existingPackageDate === packageDateUnix;

    if (!productCompatible) {
      return {
        ...baseCheck,
        productCompatible: false,
        packageDateMatch: false,
        reason: "Crate contains different product type",
      };
    }

    if (!packageDateMatch) {
      return {
        ...baseCheck,
        productCompatible: true,
        packageDateMatch: false,
        reason: "Different packaging date (shelf life mismatch)",
      };
    }

    return { ...baseCheck, productCompatible: true, packageDateMatch: true };
  } catch (error) {
    console.error("Error checking product compatibility:", error);
    return { ...baseCheck, productCompatible: false, reason: error.message };
  }
};

/**
 * Analyzes an order's pickedCratesInfo and determines restocking options
 * Returns detailed analysis for the unpack modal
 */
export const analyzeOrderRestockOptions = async (order, crateStocks = []) => {
  const pickedCratesInfo = order?.pickedCratesInfo || {};
  const analysis = {
    canRestockAllToOriginal: true,
    products: [],
    unavailableCrates: [],
    originalCrateIds: new Set(),
    totalItems: 0,
  };

  // Build a map of available crate stocks for quick lookup
  const crateStockMap = new Map();
  for (const crate of crateStocks) {
    const docId = crate.id || sanitizeCrateDocId(crate.crateId);
    crateStockMap.set(docId, crate);
    crateStockMap.set(crate.crateId, crate);
  }

  // Analyze each product in pickedCratesInfo
  for (const [productId, crateLines] of Object.entries(pickedCratesInfo)) {
    if (!Array.isArray(crateLines)) continue;

    const productAnalysis = {
      productId,
      totalQuantity: 0,
      crateLines: [],
    };

    for (const line of crateLines) {
      const crateDocId = line.crateId || line.crateDocId;
      const quantity = Number(line.quantity || 0);
      const packageDateUnix = line.packageDateUnix || null;
      const subItemProductId = line.subItemProductId || null;

      if (!crateDocId || quantity <= 0) continue;

      analysis.totalItems += quantity;
      productAnalysis.totalQuantity += quantity;
      analysis.originalCrateIds.add(crateDocId);

      // For bundle sub-items, check availability using the actual sub-item productId
      const actualProductId = subItemProductId || productId;
      const availability = await canRestockProductToCrate(crateDocId, actualProductId, packageDateUnix);
      const crateStock = crateStockMap.get(crateDocId);

      // Determine if crate needs to be restored from log (empty available crate)
      const isEmptyAvailable = availability.currentStatus === "available" && availability.currentQuantity === 0;
      const needsRestoreFromLog = isEmptyAvailable && availability.canRestock;

      const lineAnalysis = {
        crateDocId,
        quantity,
        packageDateUnix,
        subItemProductId,
        canRestockToOriginal:
          availability.canRestock && availability.productCompatible && availability.packageDateMatch,
        needsRestoreFromLog,
        availabilityReason: availability.reason,
        crateStatus: availability.currentStatus,
        crateInfo: crateStock
          ? {
              crateId: crateStock.crateId,
              currentProductId: crateStock.items?.[0]?.productId || null,
              currentQuantity: crateStock.itemsCount || 0,
            }
          : null,
      };

      if (!lineAnalysis.canRestockToOriginal) {
        analysis.canRestockAllToOriginal = false;
        analysis.unavailableCrates.push({
          crateDocId,
          productId: actualProductId,
          quantity,
          reason: availability.reason,
        });
      }

      productAnalysis.crateLines.push(lineAnalysis);
    }

    if (productAnalysis.totalQuantity > 0) {
      analysis.products.push(productAnalysis);
    }
  }

  return analysis;
};

/**
 * Executes a restock plan for unpacking an order
 * plan format: { [productId]: [ { crateDocId, quantity, isNewCrate: boolean, packageDateUnix } ] }
 * Handles restored crates (empty crates with events flushed to log) properly
 */
export const executeRestockPlan = async (plan, order, userEmail, userId, products = []) => {
  const results = {
    success: true,
    restocked: [],
    failed: [],
  };

  for (const [productId, lines] of Object.entries(plan)) {
    for (const line of lines) {
      const { crateDocId, quantity, isNewCrate, packageDateUnix, subItemProductId } = line;

      // For bundle sub-items, use the actual sub-item productId for restocking
      const actualProductId = subItemProductId || productId;
      const product = products.find((p) => p.id === actualProductId || p.productId === actualProductId);
      const productName = product?.productName || product?.name?.en || null;
      const productBarcode = product?.barcode || null;

      try {
        // First, check if crate needs to be restored from log (empty crate that was flushed)
        const crateRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_CRATE_STOCKS_TABLE,
          crateDocId,
        );

        const crateSnap = await getDoc(crateRef);
        let crateData = crateSnap.data();
        let needsRestore = false;

        if (crateSnap.exists() && crateData) {
          const itemsCount = typeof crateData.itemsCount === "number" ? crateData.itemsCount : 0;
          const items = Array.isArray(crateData.items) ? crateData.items : [];
          const status = crateData.status || "available";
          const isFixedCrate = crateData.crateType === "fixed_dumping" || crateData.isFixedDumpingCrate === true;

          // If crate is empty (available status and no items), it may have been flushed
          // We need to restore events from log before restocking
          // EXCEPT for fixed dumping crates - they should never restore old events
          // because they are meant to receive new products from cancelled orders
          if (status === "available" && itemsCount === 0 && items.length === 0 && !isFixedCrate) {
            needsRestore = true;
          }
        }

        // Restore events from log if needed
        if (needsRestore) {
          const restored = await restoreCrateEventsFromLog({ crateDocId });
          if (restored && restored.restored) {
            // Re-fetch crate data after restore
            const refreshedSnap = await getDoc(crateRef);
            crateData = refreshedSnap.data();
          }
        }

        let itemMetadata = {};

        // For new crates or restored empty crates, we need to provide full item metadata
        if (isNewCrate || needsRestore) {
          itemMetadata = {
            productId: actualProductId,
            productName,
            barcode: productBarcode,
            packageDate: packageDateUnix ? dayjs.unix(packageDateUnix).format("YYYY-MM-DD") : null,
            packageDateUnix,
            createdByUid: userId,
            createdBy: userEmail,
          };
        }

        await restockCrate({
          crateDocId,
          productId: actualProductId,
          productName,
          productBarcode,
          quantity,
          orderId: order.id,
          farmorderId: order.farmorderId,
          company: null,
          userEmail,
          userId,
          itemMetadata,
        });

        // Update countedStock: increase when restocking (unpacking order)
        try {
          await updateProductCountedStock(actualProductId, quantity);
        } catch (error) {
          console.error(`Failed to update countedStock for ${actualProductId}:`, error);
          // Don't fail the entire operation if countedStock update fails
        }

        results.restocked.push({
          productId: actualProductId,
          crateDocId,
          quantity,
          isNewCrate,
          restoredFromLog: needsRestore,
        });
      } catch (error) {
        console.error(`Failed to restock ${quantity} of ${actualProductId} to ${crateDocId}:`, error);
        results.failed.push({
          productId: actualProductId,
          crateDocId,
          quantity,
          error: error.message,
        });
        results.success = false;
      }
    }
  }

  return results;
};

/**
 * Groups picked crate lines by package date for shelf life management
 * Returns groups that can go into separate crates
 */
export const groupByPackageDate = (crateLines) => {
  const groups = {};

  for (const line of crateLines) {
    const key = line.packageDateUnix || "unknown";
    if (!groups[key]) {
      groups[key] = {
        packageDateUnix: line.packageDateUnix,
        lines: [],
        totalQuantity: 0,
      };
    }
    groups[key].lines.push(line);
    groups[key].totalQuantity += Number(line.quantity || 0);
  }

  return Object.values(groups);
};
